import React, { useState, useEffect, useContext } from "react";
import MainSelect from "../UIComponents/Select";
import { ConfigOptions, TableResponseData, RequestStatusState } from "../../App";
import { fetchPost } from "../../services/useFetch";
import { t } from "../../utils/intl";
import {
  DefaultConfigState,
  IInitialValues,
  initialValidationsState,
  ProductCategories
} from "../../const/InitialCalcConfig";
import { DictItem, ProductConfig } from "../../types/requestTypes";
import { validationConfigMap } from "../../const/InitialCalcConfig";
import { CurrencyInput } from "../UIComponents/CurrencyInput";
import { IValidationResult } from "../../const/defaultValidations";
import { SendCalcButton } from "../UIComponents/SendCalcButton";

interface IProps {
  selectedProduct: ProductCategories;
  dateOfAppSignature: string;
  setcalculationResult?: any;
  findDateConfig?: ProductConfig;
}

interface IFieldValidationState {
  id: string;
  result: IValidationResult;
  visibility: boolean;
}

interface IRunValidationState {
  nextFormData?: IInitialValues;
  nextValidationsState?: IFieldValidationState[];
}

export const ProductForm = ({ selectedProduct, dateOfAppSignature, findDateConfig }: IProps) => {
  const { templateConfigMap } = useContext(ConfigOptions);
  const { requestsStatus, setRequestsStatus } = useContext(RequestStatusState);
  const { setcalculationResult } = useContext(TableResponseData);
  const templateConfig = templateConfigMap[selectedProduct];
  const [formData, setFormData] = useState<IInitialValues>(templateConfig.initialValues);
  const [validationsState, setValidationsState] = useState<IFieldValidationState[]>(
    initialValidationsState
  );

  useEffect(() => {
    return () => {
      setcalculationResult([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    setFormData(templateConfig.initialValues);
  }, [selectedProduct, templateConfig.initialValues]);

  const runValidations = ({
    nextFormData = formData,
    nextValidationsState = validationsState
  }: IRunValidationState) => {
    if (!selectedProduct || !findDateConfig) {
      return;
    }

    const validationResults = validationConfigMap[selectedProduct].map(({ id, validations }) => {
      const results = validations.map(validationRule => {
        return validationRule(findDateConfig, nextFormData);
      });
      const result = results.find(el => el.isInvalid) || results[0];
      const lastVisibility = nextValidationsState.find(el => el.id === id)?.visibility;
      return { id, result, visibility: !!lastVisibility };
    });

    setValidationsState(validationResults);
    return validationResults;
  };

  const onBlur = (id: string) => {
    const nextValidationsState = validationsState.map(fieldValidationState =>
      fieldValidationState.id !== id
        ? fieldValidationState
        : { ...fieldValidationState, visibility: true }
    );
    runValidations({ nextValidationsState });
  };

  const onChange = (id: string, data: string | number | { value: string; label: string }) => {
    setcalculationResult([]);
    const nextFormData = {
      ...formData,
      [id]: typeof data === "object" ? data.value : data,
      productCategory: selectedProduct,
      applicationSignDate: dateOfAppSignature
    };
    setFormData(nextFormData);
    runValidations({ nextFormData });
  };

  const getCalculation = () => {
    const nextValidationsState = validationsState.map(fieldValidationState => {
      return { ...fieldValidationState, visibility: true };
    });

    const checkValidation = runValidations({ nextValidationsState });

    if (checkValidation?.find(el => el.result.isInvalid === true)) {
      return;
    }
    setRequestsStatus({ getCalculationRequest: { isLoading: true, hasError: false } });

    fetchPost(`calculate`, formData)
      .then(payload => payload.json())
      .then((data: any) => {
        setcalculationResult(data.gww);
        setRequestsStatus({ getCalculationRequest: { isLoading: false, hasError: false } });
      })
      .catch(() => {
        setRequestsStatus({ getCalculationRequest: { isLoading: false, hasError: true } });
      });
  };

  const prepareSelectOptions = (config: DefaultConfigState) => {
    const dictEntry = findDateConfig?.dicts.find(dict => dict.dictname === config.dictname);
    if (!dictEntry) return [];

    return dictEntry.items.map((el: DictItem) => {
      let labelTrKey = "";

      switch (config.dictType) {
        case "years":
          labelTrKey = `${el.code} L`;
          break;
        case "translate":
          labelTrKey = t(`dicts.${config.dictname}.${el.code}`);
          break;
        default:
          labelTrKey = el.code as string;
      }

      return { value: el.code, label: labelTrKey };
    });
  };

  const isFormValid = validationsState?.reduce((acc, el) => {
    if (el.result.isInvalid) return false;
    return acc;
  }, true);

  const hasFormCompleteData = templateConfig?.fields.reduce((acc, el) => {
    if (!formData[el.id]) return false;
    return acc;
  }, true);

  return (
    <>
      {templateConfig?.fields?.map((data: DefaultConfigState, i: number) => {
        const fieldValidationState = validationsState.find(el => el.id === data.id);
        switch (data.type) {
          case `Select`:
            const options = prepareSelectOptions(data);
            return (
              <MainSelect
                rowNumber={data.rowNumber}
                key={data.id}
                isInvalid={
                  fieldValidationState?.visibility && fieldValidationState?.result.isInvalid
                }
                isInvalidMessage={fieldValidationState?.result.invalidMessage}
                labelTrKey={data.headerTrKey}
                id={data.id}
                onBlur={() => onBlur(data.id)}
                value={options.find(el => el.value === formData[data.id])}
                onChange={(el: { value: string; label: string }) => onChange(data.id, el)}
                options={options}
              />
            );
          case `Input`:
            return (
              <CurrencyInput
                isInvalid={
                  fieldValidationState?.visibility && fieldValidationState?.result.isInvalid
                }
                isInvalidMessage={fieldValidationState?.result.invalidMessage}
                rowNumber={data.rowNumber}
                key={data.id}
                value={formData[data.id]}
                onBlur={() => onBlur(data.id)}
                onChange={(el: string) => onChange(data.id, el)}
                labelTrKey={data.headerTrKey}
                id={data.id}
              />
            );
          default:
            return null;
        }
      })}
      <SendCalcButton
        buttonType="primaryBlack"
        hasError={requestsStatus?.getCalculationRequest.hasError}
        isLoading={requestsStatus?.getCalculationRequest.isLoading}
        disabled={
          !isFormValid ||
          !hasFormCompleteData ||
          (requestsStatus ? requestsStatus?.getCalculationRequest.isLoading : false)
        }
        onClick={getCalculation}
        textTrKey={`form.calculate`}
        size={"large"}
      />
    </>
  );
};
